import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "About", href: "#mission" },
    { name: "Messages", href: "#media" },
    { name: "Bible", href: "#bible" },
    { name: "Visit", href: "#visit" },
    { name: "Leadership", href: "#leadership" },
    { name: "Portal", href: "/portal", isRoute: true },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "nav-glass shadow-sm" : "bg-transparent"
        }`}
        data-testid="navigation"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a
              href="#"
              className={`font-['Clash_Display'] font-bold text-xl tracking-tight ${
                isScrolled ? "text-black" : "text-white"
              }`}
              data-testid="nav-logo"
            >
              LIGHT CITY
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className={`font-['Satoshi'] text-sm font-medium tracking-wide transition-colors hover:opacity-70 ${
                    isScrolled ? "text-black" : "text-white"
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                  {...(link.isRoute ? {} : {})}
                >
                  {link.name}
                </a>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center gap-4">
              <a href="#visit">
                <Button
                  variant="outline"
                  className={`btn-sharp px-6 py-2 text-sm ${
                    isScrolled
                      ? "border-black text-black hover:bg-black hover:text-white"
                      : "border-white text-white hover:bg-white hover:text-black"
                  }`}
                  data-testid="nav-plan-visit-btn"
                >
                  Plan Your Visit
                </Button>
              </a>
              <a href="https://square.link/u/3pE8v6ko?src=sheet" target="_blank" rel="noopener noreferrer">
                <Button
                  className={`btn-sharp px-6 py-2 text-sm ${
                    isScrolled
                      ? "bg-black text-white hover:bg-white hover:text-black hover:shadow-[inset_0_0_0_2px_black]"
                      : "bg-white text-black hover:bg-transparent hover:text-white hover:shadow-[inset_0_0_0_2px_white]"
                  }`}
                  data-testid="nav-give-btn"
                >
                  Give
                </Button>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className={`md:hidden p-2 ${isScrolled ? "text-black" : "text-white"}`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="mobile-menu flex flex-col items-center justify-center" data-testid="mobile-menu">
          <button
            className="absolute top-6 right-6 p-2 text-black"
            onClick={() => setIsMobileMenuOpen(false)}
            data-testid="mobile-menu-close"
          >
            <X size={24} />
          </button>
          <div className="flex flex-col items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="font-['Clash_Display'] text-2xl font-semibold text-black tracking-tight"
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ))}
            <div className="flex flex-col gap-4 mt-8">
              <a href="#visit" onClick={() => setIsMobileMenuOpen(false)}>
                <Button
                  variant="outline"
                  className="btn-sharp px-8 py-3 text-sm border-black text-black"
                  data-testid="mobile-plan-visit-btn"
                >
                  Plan Your Visit
                </Button>
              </a>
              <a href="https://square.link/u/3pE8v6ko?src=sheet" target="_blank" rel="noopener noreferrer" onClick={() => setIsMobileMenuOpen(false)}>
                <Button
                  className="btn-sharp px-8 py-3 text-sm bg-black text-white"
                  data-testid="mobile-give-btn"
                >
                  Give
                </Button>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;
